import React, { useRef, useState } from 'react';
import { Upload, Image, Palette, Type, Info } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { fontOptions } from '../../data/mockData';
import { uploadImageToServer } from '../../lib/upload';

const Step2MediaStyle = ({ data, onChange }) => {
  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  const handleChange = (field, value) => {
    // Use functional updater to avoid relying on stale `data` snapshot
    onChange((prev) => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        // store local preview as fallback and immediate preview
        handleChange('logoLocalPreview', reader.result);
        handleChange('logoPreview', reader.result);
        handleChange('logo', file);
      };
      reader.readAsDataURL(file);

      // Auto-upload to server if endpoint provided via env
      const endpoint = process.env.REACT_APP_UPLOAD_ENDPOINT;
      const token = process.env.REACT_APP_UPLOAD_TOKEN;
      if (endpoint) {
        (async () => {
          try {
            handleChange('logoUploading', true);
            const result = await uploadImageToServer(file, { endpointUrl: endpoint, token });
            // debug log
            if (process.env.NODE_ENV !== 'production') {
              // eslint-disable-next-line no-console
              console.debug('logo upload result', result);
            }
            // result.url contains public URL; keep local preview as fallback
            handleChange('logoPreview', result.url);
            handleChange('logoServerFilename', result.filename);
          } catch (err) {
            console.error('Logo upload failed', err);
          } finally {
            handleChange('logoUploading', false);
          }
        })();
      }
    }
  };

  const handleBannerUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1024 * 1024) {
        alert('O banner deve ter no máximo 1MB.');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        handleChange('bannerLocalPreview', reader.result);
        handleChange('bannerPreview', reader.result);
        handleChange('banner', file);
      };
      reader.readAsDataURL(file);
      
      // Auto-upload banner if endpoint configured
      const endpoint = process.env.REACT_APP_UPLOAD_ENDPOINT;
      const token = process.env.REACT_APP_UPLOAD_TOKEN;
      if (endpoint) {
        (async () => {
          try {
            handleChange('bannerUploading', true);
            const result = await uploadImageToServer(file, { endpointUrl: endpoint, token });
            if (process.env.NODE_ENV !== 'production') {
              // eslint-disable-next-line no-console
              console.debug('banner upload result', result);
            }
            handleChange('bannerPreview', result.url);
            handleChange('bannerServerFilename', result.filename);
          } catch (err) {
            console.error('Banner upload failed', err);
          } finally {
            handleChange('bannerUploading', false);
          }
        })();
      }
    }
  };

  return (
    <div className="space-y-8">
      {/* Logo Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Image className="w-5 h-5 text-blue-500" />
          <h3 className="font-semibold text-slate-800">Logo</h3>
        </div>
        
        <div className="flex gap-4 items-start">
          <input
            ref={logoInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg"
            onChange={handleLogoUpload}
            className="hidden"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => logoInputRef.current.click()}
            className="flex items-center gap-2"
          >
            <Upload className="w-4 h-4" />
            Upload Logo
          </Button>
          
          {data.logoPreview && (
            <div className="w-16 h-16 border rounded-lg overflow-hidden bg-slate-50">
              <img
                src={data.logoPreview}
                alt="Logo preview"
                className="w-full h-full object-contain"
                style={{ borderRadius: `${data.logoBorderRadius}%` }}
                onError={(e) => {
                  if (data.logoLocalPreview) {
                    e.currentTarget.src = data.logoLocalPreview;
                    handleChange('logoPreview', data.logoLocalPreview);
                  }
                }}
              />
            </div>
          )}
        </div>
        
        <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-600">
          <div className="flex items-start gap-2 mb-2">
            <Info className="w-4 h-4 mt-0.5 text-blue-500 flex-shrink-0" />
            <span className="font-medium">Instruções:</span>
          </div>
          <ul className="list-disc list-inside space-y-1 ml-6">
            <li>Dimensões recomendadas: 100–200px de largura</li>
            <li>Formato: PNG (preferencialmente) ou JPG</li>
            <li>Use fundo transparente (PNG) para melhor resultado</li>
            <li>Evite textos muito pequenos</li>
            <li>Resolução: 72–96 DPI</li>
          </ul>
        </div>
        
        <div className="space-y-2">
          <Label className="text-slate-700">Arredondar borda do Logo: {data.logoBorderRadius}%</Label>
          <Slider
            value={[data.logoBorderRadius]}
            onValueChange={(value) => handleChange('logoBorderRadius', value[0])}
            min={0}
            max={50}
            step={1}
            className="w-full"
          />
        </div>
      </div>

      {/* Banner Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Image className="w-5 h-5 text-blue-500" />
          <h3 className="font-semibold text-slate-800">Banner Promocional</h3>
        </div>
        
        <div className="flex gap-4 items-start">
          <input
            ref={bannerInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg"
            onChange={handleBannerUpload}
            className="hidden"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => bannerInputRef.current.click()}
            className="flex items-center gap-2"
          >
            <Upload className="w-4 h-4" />
            Upload Banner
          </Button>
          
          {data.bannerPreview && (
            <div className="w-32 h-16 border rounded-lg overflow-hidden bg-slate-50">
              <img
                src={data.bannerPreview}
                alt="Banner preview"
                className="w-full h-full object-cover"
                style={{ borderRadius: `${data.bannerBorderRadius}%` }}
                onError={(e) => {
                  if (data.bannerLocalPreview) {
                    e.currentTarget.src = data.bannerLocalPreview;
                    handleChange('bannerPreview', data.bannerLocalPreview);
                  }
                }}
              />
            </div>
          )}
        </div>
        
        <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-600">
          <div className="flex items-start gap-2 mb-2">
            <Info className="w-4 h-4 mt-0.5 text-blue-500 flex-shrink-0" />
            <span className="font-medium">Instruções:</span>
          </div>
          <ul className="list-disc list-inside space-y-1 ml-6">
            <li>Dimensões recomendadas: 600px de largura</li>
            <li>Altura proporcional à largura</li>
            <li>Formato: JPG ou PNG</li>
            <li>Tamanho máximo: 1MB</li>
            <li>Evite textos muito pequenos</li>
          </ul>
        </div>
        
        <div className="space-y-2">
          <Label className="text-slate-700">Arredondar borda do Banner: {data.bannerBorderRadius}%</Label>
          <Slider
            value={[data.bannerBorderRadius]}
            onValueChange={(value) => handleChange('bannerBorderRadius', value[0])}
            min={0}
            max={50}
            step={1}
            className="w-full"
          />
        </div>
      </div>

      {/* Colors Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Palette className="w-5 h-5 text-blue-500" />
          <h3 className="font-semibold text-slate-800">Cores</h3>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-slate-700">Cor Principal</Label>
            <p className="text-xs text-slate-500">Usada no nome e destaques</p>
            <div className="flex gap-2">
              <input
                type="color"
                value={data.primaryColor}
                onChange={(e) => handleChange('primaryColor', e.target.value)}
                className="w-12 h-10 rounded cursor-pointer border border-slate-200"
              />
              <Input
                type="text"
                value={data.primaryColor}
                onChange={(e) => handleChange('primaryColor', e.target.value)}
                className="flex-1 font-mono text-sm"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label className="text-slate-700">Cor Secundária</Label>
            <p className="text-xs text-slate-500">Usada no cargo e empresa</p>
            <div className="flex gap-2">
              <input
                type="color"
                value={data.secondaryColor}
                onChange={(e) => handleChange('secondaryColor', e.target.value)}
                className="w-12 h-10 rounded cursor-pointer border border-slate-200"
              />
              <Input
                type="text"
                value={data.secondaryColor}
                onChange={(e) => handleChange('secondaryColor', e.target.value)}
                className="flex-1 font-mono text-sm"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label className="text-slate-700">Cor de Destaque</Label>
            <p className="text-xs text-slate-500">Usada nos ícones sociais</p>
            <div className="flex gap-2">
              <input
                type="color"
                value={data.accentColor}
                onChange={(e) => handleChange('accentColor', e.target.value)}
                className="w-12 h-10 rounded cursor-pointer border border-slate-200"
              />
              <Input
                type="text"
                value={data.accentColor}
                onChange={(e) => handleChange('accentColor', e.target.value)}
                className="flex-1 font-mono text-sm"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label className="text-slate-700">Cor de Fundo</Label>
            <p className="text-xs text-slate-500">Fundo da assinatura</p>
            <div className="flex gap-2">
              <input
                type="color"
                value={data.backgroundColor}
                onChange={(e) => handleChange('backgroundColor', e.target.value)}
                className="w-12 h-10 rounded cursor-pointer border border-slate-200"
              />
              <Input
                type="text"
                value={data.backgroundColor}
                onChange={(e) => handleChange('backgroundColor', e.target.value)}
                className="flex-1 font-mono text-sm"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Font Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Type className="w-5 h-5 text-blue-500" />
          <h3 className="font-semibold text-slate-800">Fonte</h3>
        </div>
        
        <Select
          value={data.fontFamily}
          onValueChange={(value) => handleChange('fontFamily', value)}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Selecione uma fonte" />
          </SelectTrigger>
          <SelectContent>
            {fontOptions.map((font) => (
              <SelectItem key={font.value} value={font.value}>
                <span style={{ fontFamily: font.value }}>{font.label}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default Step2MediaStyle;
